import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Flame, TrendingUp, Trophy, Target, BookOpen, Brain, Zap, Gamepad2, Atom, Star, Users, BarChart3, Sparkles, ArrowRight } from 'lucide-react';

const LandingPage = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen overflow-hidden">
      {/* Navigation */}
      <nav className="sticky top-0 z-50 backdrop-blur-xl bg-white/80 border-b border-white/30" data-testid="landing-nav">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            <div className="flex items-center gap-3">
              <img src="/logo.png" alt="SkillRise Logo" className="w-12 h-12 rounded-2xl object-cover shadow-lg" />
              <div>
                <h1 className="text-3xl font-black tracking-tight text-[#1E293B]">SkillRise</h1>
                <p className="text-[10px] font-semibold text-[#64748B] tracking-widest uppercase">by Ekamraj Productions</p>
              </div>
            </div>
            <div className="flex gap-3">
              <button data-testid="nav-login-btn" onClick={() => navigate('/login')} className="px-6 py-2.5 rounded-2xl font-semibold text-[#1E293B] hover:bg-slate-100 transition-colors">Login</button>
              <button data-testid="nav-signup-btn" onClick={() => navigate('/signup')} className="px-6 py-2.5 bg-gradient-to-r from-[#FF6B6B] via-[#FFC03D] to-[#4D96FF] text-white rounded-2xl font-bold shadow-lg hover:shadow-xl hover:scale-105 transition-all active:scale-95">Get Started</button>
            </div>
          </div>
        </div>
      </nav>

      {/* HERO SECTION - Vibrant & Colorful */}
      <section className="relative bg-gradient-to-br from-[#4D96FF] via-[#6C63FF] to-[#A493FF] overflow-hidden">
        {/* Animated background shapes */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute -top-20 -left-20 w-96 h-96 bg-[#FF6B6B]/20 rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute top-40 right-10 w-72 h-72 bg-[#FFC03D]/20 rounded-full blur-3xl" style={{animation: 'pulse 3s ease-in-out infinite 1s'}}></div>
          <div className="absolute -bottom-32 left-1/3 w-80 h-80 bg-[#38E54D]/15 rounded-full blur-3xl" style={{animation: 'pulse 4s ease-in-out infinite 0.5s'}}></div>
          {/* Floating icons */}
          <div className="absolute top-20 left-[10%] text-white/10 float-animation"><Brain className="w-20 h-20" /></div>
          <div className="absolute top-32 right-[15%] text-white/10" style={{animation: 'float 4s ease-in-out infinite 1s'}}><Trophy className="w-16 h-16" /></div>
          <div className="absolute bottom-20 left-[20%] text-white/10" style={{animation: 'float 3.5s ease-in-out infinite 0.5s'}}><Atom className="w-14 h-14" /></div>
          <div className="absolute bottom-32 right-[25%] text-white/10" style={{animation: 'float 5s ease-in-out infinite 1.5s'}}><Star className="w-12 h-12" /></div>
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-24 pb-28">
          <div className="text-center">
            <div className="inline-block mb-8">
              <span className="px-6 py-3 bg-white/15 backdrop-blur-md text-white rounded-full text-sm font-bold tracking-wide uppercase border border-white/20 shadow-lg">
                <Sparkles className="w-4 h-4 inline mr-2" />CBSE Aligned &bull; Grades 6-8 &bull; 15+ Games
              </span>
            </div>
            <h2 className="text-5xl sm:text-6xl lg:text-7xl font-black tracking-tight text-white mb-8 leading-tight drop-shadow-lg">
              Watch Your Skills
              <br />
              <span className="relative">
                <span className="bg-gradient-to-r from-[#FFC03D] via-[#FF6B6B] to-[#FFC03D] bg-clip-text text-transparent">Rise Every Day</span>
                <svg className="absolute -bottom-2 left-0 w-full" height="12" viewBox="0 0 400 12" fill="none">
                  <path d="M2 8C60 2 140 2 200 6C260 10 340 4 398 8" stroke="#FFC03D" strokeWidth="4" strokeLinecap="round" opacity="0.6"/>
                </svg>
              </span>
            </h2>
            <p className="text-xl sm:text-2xl text-white/85 max-w-3xl mx-auto mb-12 leading-relaxed font-medium">
              India's smartest learning platform. Track your progress in real-time, play brain games, and see your accuracy improve with every question.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <button data-testid="hero-cta-btn" onClick={() => navigate('/signup')}
                className="px-12 py-5 bg-[#FFC03D] hover:bg-[#E6A820] text-[#1E293B] rounded-3xl text-xl font-black shadow-2xl hover:shadow-[#FFC03D]/40 hover:scale-105 transition-all active:scale-95 border-b-4 border-[#E6A820] active:border-b-0 active:translate-y-1 flex items-center gap-3">
                Start Learning Free <ArrowRight className="w-6 h-6" />
              </button>
              <button onClick={() => navigate('/login')}
                className="px-10 py-5 bg-white/10 backdrop-blur-md border-2 border-white/30 text-white rounded-3xl text-xl font-bold hover:bg-white/20 transition-all">
                I have an account
              </button>
            </div>
          </div>

          {/* Stats Bar */}
          <div className="mt-20 grid grid-cols-2 md:grid-cols-4 gap-4 max-w-4xl mx-auto">
            <StatBubble icon={<BookOpen className="w-6 h-6" />} value="150+" label="CBSE Questions" />
            <StatBubble icon={<Gamepad2 className="w-6 h-6" />} value="15" label="Brain Games" />
            <StatBubble icon={<BarChart3 className="w-6 h-6" />} value="3" label="Subjects" />
            <StatBubble icon={<Users className="w-6 h-6" />} value="Free" label="For Everyone" />
          </div>
        </div>

        {/* Wave divider */}
        <div className="absolute bottom-0 left-0 right-0">
          <svg viewBox="0 0 1440 100" fill="none" className="w-full"><path d="M0 40C240 80 480 0 720 40C960 80 1200 20 1440 50V100H0V40Z" fill="#F4F8FB"/></svg>
        </div>
      </section>

      {/* Feature Cards - Bento Grid */}
      <section className="bg-[#F4F8FB] py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h3 className="text-4xl sm:text-5xl font-black text-[#1E293B] mb-4">Why Students Love SkillRise</h3>
            <p className="text-xl text-[#64748B]">It's not just studying - it's growing!</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <FeatureCard icon={<TrendingUp className="w-8 h-8" />} bgColor="bg-gradient-to-br from-blue-400 to-blue-600" title="Real Growth Tracking" description="See your accuracy jump from 65% to 82% in just 2 weeks" />
            <FeatureCard icon={<Brain className="w-8 h-8" />} bgColor="bg-gradient-to-br from-green-400 to-green-600" title="Smart Practice" description="CBSE questions that adapt to your level automatically" />
            <FeatureCard icon={<Flame className="w-8 h-8" />} bgColor="bg-gradient-to-br from-orange-400 to-red-500" title="Daily Streaks" description="Build consistency with Duolingo-style challenges" />
            <FeatureCard icon={<Target className="w-8 h-8" />} bgColor="bg-gradient-to-br from-purple-400 to-purple-600" title="Weak Area Detector" description="Automatically spots topics that need extra work" />
          </div>
        </div>
      </section>

      {/* Brain Arena Showcase */}
      <section className="bg-[#F4F8FB] pb-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-gradient-to-br from-[#1E293B] to-[#334155] rounded-3xl p-10 md:p-16 overflow-hidden relative">
            <div className="absolute top-0 right-0 w-96 h-96 bg-gradient-to-br from-[#FF6B6B]/20 to-[#FFC03D]/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/3"></div>
            <div className="absolute bottom-0 left-0 w-72 h-72 bg-gradient-to-tr from-[#4D96FF]/20 to-[#A493FF]/10 rounded-full blur-3xl translate-y-1/2 -translate-x-1/3"></div>
            
            <div className="relative grid md:grid-cols-2 gap-10 items-center">
              <div>
                <div className="inline-flex items-center gap-2 bg-white/10 px-4 py-2 rounded-full text-white/80 text-sm font-bold mb-6 border border-white/10">
                  <Gamepad2 className="w-4 h-4" /> BRAIN ARENA
                </div>
                <h3 className="text-4xl sm:text-5xl font-black text-white mb-6 leading-tight">15 Games That Make You Smarter</h3>
                <p className="text-lg text-white/70 mb-8 leading-relaxed">Build atoms, slice fraction pizzas, race through times tables, unscramble words & more. Learning has never been this fun!</p>
                <button onClick={() => navigate('/signup')} data-testid="games-cta-btn"
                  className="px-8 py-4 bg-gradient-to-r from-[#FF6B6B] via-[#FFC03D] to-[#4D96FF] text-white rounded-2xl font-bold text-lg hover:scale-105 transition-all shadow-xl active:scale-95 flex items-center gap-2">
                  Play Games Now <ArrowRight className="w-5 h-5" />
                </button>
              </div>
              <div className="grid grid-cols-3 gap-3">
                {[
                  { name: 'Atom Builder', icon: <Atom className="w-6 h-6" />, color: 'from-pink-500 to-rose-600' },
                  { name: 'Math Blitz', icon: <Zap className="w-6 h-6" />, color: 'from-blue-500 to-indigo-600' },
                  { name: 'Fraction Pizza', icon: <span className="text-xl">🍕</span>, color: 'from-orange-500 to-red-600' },
                  { name: 'Word Scramble', icon: <span className="text-xl">🔤</span>, color: 'from-purple-500 to-violet-600' },
                  { name: 'Body Explorer', icon: <span className="text-xl">🫀</span>, color: 'from-rose-500 to-pink-600' },
                  { name: 'Spell Bee', icon: <span className="text-xl">🐝</span>, color: 'from-lime-500 to-green-600' },
                  { name: 'Circuit Build', icon: <span className="text-xl">🔌</span>, color: 'from-yellow-500 to-amber-600' },
                  { name: 'Number Ninja', icon: <span className="text-xl">🥷</span>, color: 'from-emerald-500 to-teal-600' },
                  { name: 'Story Builder', icon: <span className="text-xl">📖</span>, color: 'from-sky-500 to-cyan-600' },
                ].map((game, i) => (
                  <div key={i} className={`bg-gradient-to-br ${game.color} rounded-2xl p-4 text-white text-center hover:scale-110 transition-all cursor-pointer shadow-lg`}
                    style={{animationDelay: `${i * 0.1}s`}}>
                    <div className="mb-1">{game.icon}</div>
                    <p className="text-[10px] font-bold leading-tight">{game.name}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="bg-[#F4F8FB] py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h3 className="text-4xl sm:text-5xl font-black text-[#1E293B] mb-4">How SkillRise Works</h3>
            <p className="text-xl text-[#64748B]">Not just lessons -- real progress you can see and measure</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <StepCard step="1" color="from-blue-500 to-blue-600" icon={<BookOpen className="w-10 h-10" />} title="Answer CBSE Questions" description="Practice Math, Science, and English at your level from Class 6-8 syllabus" />
            <StepCard step="2" color="from-green-500 to-emerald-600" icon={<TrendingUp className="w-10 h-10" />} title="Watch Your Stats Rise" description="See your speed, accuracy, and Elo rating improve in real-time" />
            <StepCard step="3" color="from-[#FFC03D] to-orange-500" icon={<Trophy className="w-10 h-10" />} title="Level Up & Win" description="Earn badges, climb leaderboards, play brain games, master topics" />
          </div>
        </div>
      </section>

      {/* Growth Comparison */}
      <section className="bg-[#F4F8FB] pb-20">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-3xl p-10 border-2 border-slate-100 shadow-xl">
            <h3 className="text-3xl font-black text-[#1E293B] text-center mb-10">The SkillRise Difference</h3>
            <div className="grid md:grid-cols-2 gap-8">
              <div className="bg-red-50 rounded-2xl p-6 border-2 border-red-200">
                <p className="text-red-500 font-bold text-sm uppercase tracking-wider mb-4">Other Platforms Say</p>
                <div className="space-y-3">
                  <CompareItem text="Complete Chapter 3" isOld />
                  <CompareItem text="You scored 7/10" isOld />
                  <CompareItem text="Practice more questions" isOld />
                </div>
              </div>
              <div className="bg-green-50 rounded-2xl p-6 border-2 border-green-300">
                <p className="text-green-600 font-bold text-sm uppercase tracking-wider mb-4">SkillRise Says</p>
                <div className="space-y-3">
                  <CompareItem text="Your problem-solving speed improved by 25% this week!" />
                  <CompareItem text="Math Accuracy: 65% → 82% in 2 weeks" />
                  <CompareItem text="You'll master Algebra in about 10 days!" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-[#F4F8FB] pb-20">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-gradient-to-br from-[#FF6B6B] via-[#FFC03D] to-[#4D96FF] rounded-3xl p-12 text-center shadow-2xl relative overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-tr from-transparent via-white/10 to-transparent"></div>
            <div className="relative">
              <h3 className="text-4xl sm:text-5xl font-black text-white mb-6">Ready to See Your Growth?</h3>
              <p className="text-xl text-white/90 mb-8 max-w-2xl mx-auto">Join students already tracking their progress, playing brain games, and building better learning habits.</p>
              <button data-testid="cta-signup-btn" onClick={() => navigate('/signup')}
                className="px-12 py-5 bg-white text-[#1E293B] rounded-3xl text-xl font-black hover:scale-105 transition-all shadow-xl border-b-4 border-slate-200 active:border-b-0 active:translate-y-1 active:scale-95">
                Start Free Today <ArrowRight className="w-6 h-6 inline ml-2" />
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#1E293B]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="flex flex-col items-center gap-5">
            <div className="flex items-center gap-3">
              <img src="/logo.png" alt="SkillRise Logo" className="w-10 h-10 rounded-xl object-cover" />
              <div>
                <p className="text-lg font-black text-white">SkillRise</p>
                <p className="text-[10px] font-semibold text-slate-400 tracking-widest uppercase">by Ekamraj Productions</p>
              </div>
            </div>
            <div className="flex gap-6 text-slate-400 text-sm font-semibold">
              <span>Math</span><span>&bull;</span><span>Science</span><span>&bull;</span><span>English</span><span>&bull;</span><span>Brain Games</span>
            </div>
            <p className="text-xs text-slate-500">&copy; 2026 Ekamraj Productions. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

const StatBubble = ({ icon, value, label }) => (
  <div className="bg-white/10 backdrop-blur-md border border-white/15 rounded-2xl p-4 text-center text-white hover:bg-white/15 transition-all">
    <div className="flex justify-center mb-2 opacity-80">{icon}</div>
    <p className="text-3xl font-black">{value}</p>
    <p className="text-xs font-semibold opacity-70 uppercase tracking-wider">{label}</p>
  </div>
);

const FeatureCard = ({ icon, bgColor, title, description }) => (
  <div className="group bg-white rounded-3xl p-6 border-2 border-slate-100 hover:-translate-y-2 transition-all duration-300 hover:shadow-2xl">
    <div className={`w-16 h-16 ${bgColor} rounded-2xl flex items-center justify-center text-white mb-4 group-hover:scale-110 transition-transform shadow-lg`}>
      {icon}
    </div>
    <h4 className="text-xl font-bold text-[#1E293B] mb-2">{title}</h4>
    <p className="text-[#64748B]">{description}</p>
  </div>
);

const StepCard = ({ step, color, icon, title, description }) => (
  <div className="relative bg-white rounded-3xl p-8 border-2 border-slate-100 hover:-translate-y-2 transition-all duration-300 hover:shadow-xl">
    <div className={`absolute -top-6 left-8 w-12 h-12 bg-gradient-to-br ${color} rounded-2xl flex items-center justify-center text-white text-2xl font-black shadow-lg`}>
      {step}
    </div>
    <div className="text-[#4D96FF] mb-4 mt-4">{icon}</div>
    <h4 className="text-2xl font-bold text-[#1E293B] mb-3">{title}</h4>
    <p className="text-lg text-[#64748B]">{description}</p>
  </div>
);

const CompareItem = ({ text, isOld }) => (
  <div className={`flex items-start gap-3 p-3 rounded-xl ${isOld ? 'bg-red-100/50' : 'bg-green-100/50'}`}>
    <span className={`mt-0.5 flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center text-white text-xs font-bold ${isOld ? 'bg-red-400' : 'bg-green-500'}`}>
      {isOld ? '✕' : '✓'}
    </span>
    <p className={`font-semibold ${isOld ? 'text-red-700' : 'text-green-800'}`}>{text}</p>
  </div>
);

export default LandingPage;
